import Image, { type StaticImageData } from "next/image";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import configuratorDock from "@assets/images/shop/configurator-dock.webp";
import configuratorMatic from "@assets/images/shop/configurator-matic.webp";
import { cn } from "@/lib/utils";
import { FLOOR_PACKAGES } from "@/views/shop/floorPackages";

type ConfiguratorGalleryProps = {
  floorPackageId: string;
  className?: string;
};

type Center = {
  x: string;
  y: string;
};

type GalleryLayout = {
  docks: readonly Center[];
  matics: readonly Center[];
};

/**
 * Each render is described by its own file, not by the layout: the frame's
 * aspect, and where the product's body sits inside that frame. Both renders
 * carry an asymmetric shadow and empty margin, so a frame centred on a layout
 * point does not put the product on it — and every `Center` below is a product
 * body, since what the gallery composes is products.
 *
 * The body figures are measured from each file's alpha channel, opaque pixels
 * only, so the soft shadow is excluded. Regenerate after any asset swap:
 * `node scripts/measure-render-body.mjs <file>`. `renderWidth` then only has to
 * keep the body the size the design asks for; it is not a property of the file.
 */
export type ProductProfile = {
  aspectClassName: string;
  bodyCenterX: number;
  bodyCenterY: number;
  renderWidth: string;
  staticRenderWidth: string;
};

export const MATIC: ProductProfile = {
  aspectClassName: "[aspect-ratio:2800/2726]",
  bodyCenterX: 0.3834,
  bodyCenterY: 0.4868,
  renderWidth: "52.86%",
  staticRenderWidth: "98.11%",
};

export const DOCK: ProductProfile = {
  aspectClassName: "[aspect-ratio:1800/1527]",
  bodyCenterX: 0.4133,
  bodyCenterY: 0.4627,
  renderWidth: "31.23%",
  staticRenderWidth: "40.29%",
};

const bodyOffset = ({ bodyCenterX, bodyCenterY }: ProductProfile) =>
  `translate(${((0.5 - bodyCenterX) * 100).toFixed(2)}%, ${(
    (0.5 - bodyCenterY) *
    100
  ).toFixed(2)}%)`;

const SINGLE_ROW_Y = "50%";
const SINGLE_DOCK_X = "30%";
const SINGLE_MATIC_X = "70%";

const TWO_ITEM_LEFT_X = "34%";
const TWO_ITEM_RIGHT_X = "66%";
const TOP_ROW_Y = "34.02%";
const BOTTOM_ROW_Y = "66.29%";
const CENTERED_ROW_X = "50%";

const SECOND_DOCK_CORNER_START: Center = { x: "128%", y: "-25%" };
const SECOND_MATIC_CORNER_START: Center = { x: "-28%", y: "125%" };

// Long enough to read the count, short enough not to hold up the next pick.
const GALLERY_MOTION_DURATION_SECONDS = 0.7;
const EASE_OUT_HEAVY = [0.16, 1, 0.3, 1] as const;
const GALLERY_TRANSITION = {
  duration: GALLERY_MOTION_DURATION_SECONDS,
  ease: EASE_OUT_HEAVY,
} as const;
const INSTANT_TRANSITION = { duration: 0 } as const;

const GALLERY_LAYOUTS = {
  oneMaticOneDock: {
    docks: [{ x: SINGLE_DOCK_X, y: SINGLE_ROW_Y }],
    matics: [{ x: SINGLE_MATIC_X, y: SINGLE_ROW_Y }],
  },
  oneMaticTwoDocks: {
    docks: [
      { x: TWO_ITEM_LEFT_X, y: TOP_ROW_Y },
      { x: TWO_ITEM_RIGHT_X, y: TOP_ROW_Y },
    ],
    matics: [{ x: CENTERED_ROW_X, y: BOTTOM_ROW_Y }],
  },
  twoMaticsTwoDocks: {
    docks: [
      { x: TWO_ITEM_LEFT_X, y: TOP_ROW_Y },
      { x: TWO_ITEM_RIGHT_X, y: TOP_ROW_Y },
    ],
    matics: [
      { x: TWO_ITEM_RIGHT_X, y: BOTTOM_ROW_Y },
      { x: TWO_ITEM_LEFT_X, y: BOTTOM_ROW_Y },
    ],
  },
} as const satisfies Record<string, GalleryLayout>;

type ProductRenderProps = {
  image: StaticImageData;
  profile: ProductProfile;
  center: Center;
  width: string;
  sizes: string;
  transition: typeof GALLERY_TRANSITION | typeof INSTANT_TRANSITION;
  cornerStart?: Center;
  motionEnabled?: boolean;
  elevated?: boolean;
};

type ProductImageProps = Pick<
  ProductRenderProps,
  "image" | "profile" | "sizes"
>;

export const ProductImage = ({ image, profile, sizes }: ProductImageProps) => (
  <div
    className={`relative w-full -translate-x-1/2 -translate-y-1/2 ${profile.aspectClassName}`}
  >
    <Image
      src={image}
      alt=""
      fill
      sizes={sizes}
      className="pointer-events-none select-none object-contain"
      style={{ transform: bodyOffset(profile) }}
    />
  </div>
);

const ProductRender = ({
  image,
  profile,
  center,
  width,
  sizes,
  transition,
  cornerStart,
  motionEnabled = false,
  elevated = false,
}: ProductRenderProps) => (
  <motion.div
    initial={
      cornerStart !== undefined && motionEnabled
        ? { left: cornerStart.x, top: cornerStart.y }
        : false
    }
    animate={{ left: center.x, top: center.y }}
    {...(cornerStart === undefined
      ? {}
      : {
          exit: motionEnabled
            ? { left: cornerStart.x, top: cornerStart.y }
            : { left: center.x, top: center.y },
        })}
    transition={transition}
    className="absolute"
    style={elevated ? { width, zIndex: 1 } : { width }}
  >
    <ProductImage image={image} profile={profile} sizes={sizes} />
  </motion.div>
);

/**
 * The package's renders, counted out: a second dock or Matic slides in from
 * a corner when a package adds one, at every width (reduced motion: it
 * simply appears).
 */
const PackageGallery = ({
  floorPackageId,
}: Pick<ConfiguratorGalleryProps, "floorPackageId">) => {
  const prefersReducedMotion = useReducedMotion();
  const motionEnabled = prefersReducedMotion === false;
  const transition = motionEnabled ? GALLERY_TRANSITION : INSTANT_TRANSITION;
  const floorPackage =
    FLOOR_PACKAGES.find(({ id }) => id === floorPackageId) ?? FLOOR_PACKAGES[0];
  const maticCount = floorPackage.maticCount;
  const dockCount = floorPackage.maticCount + floorPackage.extraDockCount;
  const layout =
    maticCount === 2
      ? GALLERY_LAYOUTS.twoMaticsTwoDocks
      : dockCount === 2
        ? GALLERY_LAYOUTS.oneMaticTwoDocks
        : GALLERY_LAYOUTS.oneMaticOneDock;

  return (
    <>
      <ProductRender
        image={configuratorMatic}
        profile={MATIC}
        center={layout.matics[0]}
        width={MATIC.renderWidth}
        sizes="(min-width: 1280px) 44vw, 60vw"
        transition={transition}
        elevated
      />
      <ProductRender
        image={configuratorDock}
        profile={DOCK}
        center={layout.docks[0]}
        width={DOCK.renderWidth}
        sizes="(min-width: 1280px) 24vw, 34vw"
        transition={transition}
      />

      <AnimatePresence initial={false}>
        {dockCount === 2 && (
          <ProductRender
            key="second-dock"
            image={configuratorDock}
            profile={DOCK}
            // Rendered only when dockCount === 2.
            center={layout.docks[1]!}
            cornerStart={SECOND_DOCK_CORNER_START}
            width={DOCK.renderWidth}
            sizes="(min-width: 1280px) 24vw, 34vw"
            transition={transition}
            motionEnabled={motionEnabled}
          />
        )}
        {maticCount === 2 && (
          <ProductRender
            key="second-matic"
            image={configuratorMatic}
            profile={MATIC}
            // Rendered only when maticCount === 2.
            center={layout.matics[1]!}
            cornerStart={SECOND_MATIC_CORNER_START}
            width={MATIC.renderWidth}
            sizes="(min-width: 1280px) 44vw, 60vw"
            transition={transition}
            motionEnabled={motionEnabled}
          />
        )}
      </AnimatePresence>
    </>
  );
};

/**
 * The combination's renders: Matic and its docks, composed as percentages of
 * Figma's desktop frame (1059:1117) at every width, so a package's count
 * reads below `sd` too. Below it the box stops short of the frame's foot,
 * which the carousel pager takes.
 */
const ConfiguratorGallery = ({
  floorPackageId,
  className,
}: ConfiguratorGalleryProps) => (
  <div
    aria-hidden="true"
    className={cn(
      "absolute left-1/2 top-0 h-[84%] max-w-full -translate-x-1/2 [aspect-ratio:1059/1117] sd:h-full",
      className,
    )}
  >
    <PackageGallery floorPackageId={floorPackageId} />
  </div>
);

export default ConfiguratorGallery;
